import { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import {
  type AmountType,
  AmountTypeValues,
  type AmountWithFee,
  type PaymentAmount,
  type PaymentMethodOption,
  type PaymentOptions,
} from '../domain/types'
import { type AmountOptionApi, type AmountTypeApi, AmountTypeApiValues, type PaymentOptionsApi } from './types'
import { mapVoucherApiToDomain } from './vouchers/voucherMapper'

const amountTypeMap: Record<Exclude<AmountTypeApi, 'CustomPartial'>, AmountType> = {
  Full: AmountTypeValues.FULL,
  Partial: AmountTypeValues.DEPOSIT,
  MinDeposit: AmountTypeValues.MIN_DEPOSIT,
}

function mapAmountType(type: Exclude<AmountTypeApi, 'CustomPartial'>): AmountType {
  return amountTypeMap[type]
}

function mapAmountWithFees(amountWithFees: AmountOptionApi['amountsWithFees']): AmountWithFee[] | undefined {
  if (!amountWithFees) {
    return undefined
  }

  return amountWithFees.map(({ paymentMethodType, amount }) => ({
    paymentMethodType: paymentMethodType.toLowerCase(),
    amount,
  }))
}

function applyAmountWithFees(target: PaymentAmount, option: AmountOptionApi): PaymentAmount {
  const amountWithFees = mapAmountWithFees(option.amountsWithFees)

  if (!amountWithFees) {
    return target
  }

  return {
    ...target,
    amountWithFees,
  }
}

function mapPaymentAmounts(amountOptions: AmountOptionApi[]): PaymentAmount[] {
  const customPartial = amountOptions.find(opt => opt.type === AmountTypeApiValues.CUSTOM_PARTIAL)
  const hasPartial = amountOptions.some(opt => opt.type === AmountTypeApiValues.PARTIAL)

  return amountOptions.reduce<PaymentAmount[]>((paymentAmounts, option) => {
    if (option.type === AmountTypeApiValues.CUSTOM_PARTIAL) {
      if (!hasPartial) {
        paymentAmounts.push(applyAmountWithFees({
          type: AmountTypeValues.DEPOSIT,
          amount: option.amount,
          availablePaymentMethodTypes: option.availablePaymentMethodTypes as PaymentMethod[],
          allowsCustomAmount: true,
          availablePaymentMethodTypesCustomPartial: option.availablePaymentMethodTypes as PaymentMethod[],
          customPartialMinAmount: option.amount,
          isDownPayment: false,
        }, option))
      }

      return paymentAmounts
    }

    const base: PaymentAmount = {
      type: mapAmountType(option.type),
      amount: option.amount,
      availablePaymentMethodTypes: option.availablePaymentMethodTypes as PaymentMethod[],
    }

    if (option.type === AmountTypeApiValues.PARTIAL && customPartial) {
      base.allowsCustomAmount = true
      base.customPartialMinAmount = customPartial.amount
      base.availablePaymentMethodTypesCustomPartial = customPartial.availablePaymentMethodTypes as PaymentMethod[]
    }

    if (option.type === AmountTypeApiValues.PARTIAL) {
      base.isDownPayment = true
    }

    paymentAmounts.push(applyAmountWithFees(base, option))
    return paymentAmounts
  }, [])
}

export function mapPaymentOptions(apiResponse: PaymentOptionsApi): PaymentOptions {
  return {
    amountAlreadyPaid: apiResponse?.amountMoneyAlreadyPaid?.amount ?? null,
    downPaymentAlreadyCovered: apiResponse.downPaymentAlreadyCovered,
    paymentProvider: apiResponse.paymentProvider,
    partialMaxAmount: apiResponse.partialPayment?.maximumPartialAmount.amount ?? 0,
    paymentAmounts: mapPaymentAmounts(apiResponse.amountOptions),
    nextPayments: apiResponse.nextPayments
      ? {
          fullPaymentDueDate: apiResponse.nextPayments?.fullPayment?.dueDate,
          downPaymentDueDate: apiResponse.nextPayments?.downPayment?.dueDate,
        }
      : undefined,
    paymentMethods: apiResponse.paymentMethods.map<PaymentMethodOption>(method => ({
      paymentMethodType: method.paymentMethodType as PaymentMethod,
      transactionType: method.transactionType,
      groupCode: method.groupCode,
      percentageFee: method.percentageFee,
      fixedFee: method.fixedFee,
    })),
    vouchers: apiResponse.availableVouchers?.map(mapVoucherApiToDomain),
  }
}
